import React, { ChangeEvent, FC, useContext, useEffect, useRef, useState } from "react";
import { mutate } from "swr";
import { AppContext } from "../../../app-context";
import { updateProfile } from "../../apis/update-profile";
import { useLayout } from "../../hooks/use-layout";

import { Dialog, listItem } from "@in/component-library";
import { DEFAULT_COUNTRY_CODE } from "../../../utils/constants";
import countries from "../../../utils/countries";
import { cleanupPhoneNumber, formatPhoneNumber } from "../../../utils/phoneNumberFormat";
import { AccountV1 } from "../../models/account-v1";
import AccountSearch from "../account-search/account-search";
import AddAccount from "../add-account/add-account";
import AddProfileSucceeded from "../add-profile-succeeded/add-profile-succeeded";
import CreateForeignAccount from "../create-foreign-account/create-foreign-account";
import VerifyEmail from "../verify-email-new-account/verify-email-new-account";

export enum Steps {
  AccountSearch = "accountSearch",
  CreateForeignAccount = "createForeignAccount",
  AddAccount = "addAccount",
  VerifyEmail = "verifyEmail",
  AddProfileSucceeded = "addProfileSucceeded"
}

interface AddProfileModalProps {
  onClose: () => void;
  disableSearch: boolean;
  disablePersonal: boolean;
  initialStep: Steps;
  updateActiveStep: (activeStep: Steps) => void;
  goToPreviousUrl: () => void;
  updateAccountList: (accountId: string, isPersonal: boolean, name: string) => void;
  personalAccount?: AccountV1;
  showModal: boolean;
}

const AddProfileModal: FC<AddProfileModalProps> = ({
  onClose,
  disableSearch,
  disablePersonal,
  updateActiveStep,
  initialStep = Steps.AccountSearch,
  goToPreviousUrl,
  updateAccountList,
  personalAccount,
  showModal
}) => {
  const { translations } = useLayout();
  const { account, selectAccount } = useContext(AppContext);

  const [selectedAccountInfo, setSelectedAccountInfo] = useState<listItem>();
  const [email, setEmail] = useState<string>("");
  const [countryCode, setCountryCode] = useState(DEFAULT_COUNTRY_CODE);
  const [phoneNumber, setPhoneNumber] = useState<string>("");

  const [isSaving, setIsSaving] = useState(false);
  const [emailError, setEmailError] = useState<string>();
  const [phoneError, setPhoneError] = useState<string>();
  const [profile, setProfile] = useState<null | {
    email: string;
    phoneNumber: string;
    countryCode: string;
  }>(null);

  const [isPersonal, setIsPersonal] = useState<boolean>(false);

  const dialogRef = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    setActiveStep(initialStep);
  }, [initialStep]);

  useEffect(() => {
    if (showModal) {
      dialogRef.current?.showModal();
    } else {
      dialogRef.current?.close();
    }
  }, [showModal]);

  const title = {
    accountSearch: translations.chooseAccountAddCompanyTitle,
    createForeignAccount: translations.chooseAccountAddCompanyTitle,
    addAccount: account?.isPersonal
      ? translations.chooseAccountAddPersonalTitle
      : translations.chooseAccountAddCompanyTitle,
    verifyEmail: translations.verifyEmailHeader,
    addProfileSucceeded: translations.confirmationHeader
  };

  const style = {
    accountSearch: "choose-account__modal",
    createForeignAccount: "choose-account-__modal-foreign",
    addAccount: "choose-account__modal",
    verifyEmail: "email-new-account__modal",
    addProfileSucceeded: "add-profile-succeeded__modal"
  };

  const [activeStep, setActiveStep] = useState<Steps>(Steps.AccountSearch);

  useEffect(() => {
    updateActiveStep(activeStep);
  }, [activeStep, updateActiveStep]);

  const goToStep = (step: Steps) => {
    setActiveStep(step);
  };

  const setSelectedAccount = async (accountId: string, isExternal: boolean) => {
    setIsSaving(true);

    const response = await selectAccount(accountId, isExternal);
    if (response.isVerified) {
      goToPreviousUrl();
    } else {
      setIsPersonal(response.isPersonal);
      goToStep(Steps.AddAccount);
    }
    setIsSaving(false);
  };

  const setAccountInfo = async (organizationNumber: string, address: string, name: string) => {
    setSelectedAccountInfo({ organizationNumber, address, name });
  };

  const submitContactInformation = async (email: string, countryCode: string, phoneNumber: string) => {
    setIsSaving(true);
    setEmailError(undefined);
    setPhoneError(undefined);
    try {
      const response = await updateProfile({
        email,
        countryCode,
        phoneNumber
      });

      if (response.code === 200) {
        refreshContactInputs();
        if (response.body.isVerified) {
          updateContactInfo(email, phoneNumber, countryCode);
          mutate("accounts/selected", response.body.account);
          mutate("userAccounts");
          setActiveStep(Steps.AddProfileSucceeded);
          onSuccess();
        } else {
          updateContactInfo(email, phoneNumber, countryCode);
          if (activeStep !== Steps.VerifyEmail) {
            goToStep(Steps.VerifyEmail);
          }
        }
      } else if (response.code === 409) {
        setEmailError(translations.verifyEmailAddressAlreadyRegisteredError);
      } else {
        setEmailError(response.body.emailError);
        setPhoneError(response.body.phoneError);
      }
    } catch {
      setEmailError(translations.errorMessage);
    }

    setIsSaving(false);
  };

  const refreshContactInputs = () => {
    setEmail("");
    setPhoneNumber("");
    setCountryCode(DEFAULT_COUNTRY_CODE);
  };

  const updateContactInfo = (email: string, phoneNumber: string, countryCode: string) => {
    setProfile({ email, phoneNumber, countryCode });
  };

  const onSuccess = () => {
    selectedAccountInfo &&
      typeof isPersonal === "boolean" &&
      updateAccountList(selectedAccountInfo.organizationNumber, isPersonal, selectedAccountInfo.name);
    mutate(["accounts", !disablePersonal, !disableSearch]);
    mutate("userAccounts");
  };

  const onCloseModal = () => {
    onClose();
    refreshContactInputs();
    setEmailError(undefined);
    setPhoneError(undefined);
    setActiveStep(Steps.AccountSearch);
    setSelectedAccountInfo(undefined);
  };

  return (
    <Dialog
      className={style[activeStep]}
      title={title[activeStep]}
      ref={dialogRef}
      onClose={onCloseModal}
      closeButton={{
        onClick: () => dialogRef.current?.close(),
        "aria-label": translations.close
      }}
    >
      {activeStep === Steps.AccountSearch && (
        <AccountSearch
          onClose={onClose}
          selectAccount={setSelectedAccount}
          accountInfo={setAccountInfo}
          isSaving={isSaving}
          toCreateForeignAccount={() => goToStep(Steps.CreateForeignAccount)}
        />
      )}

      {activeStep === Steps.CreateForeignAccount && (
        <CreateForeignAccount
          onClose={onClose}
          selectAccount={setSelectedAccount}
          isSaving={isSaving}
          setAccountInfo={setAccountInfo}
        />
      )}

      {activeStep === Steps.AddAccount && (
        <AddAccount
          onClose={onCloseModal}
          submit={() => submitContactInformation(email, countryCode, cleanupPhoneNumber(phoneNumber))}
          isSaving={isSaving}
          emailError={emailError}
          phoneError={phoneError}
          selectedAccount={selectedAccountInfo}
          updateEmail={(e: ChangeEvent<HTMLInputElement>) => setEmail((e.target as HTMLInputElement).value)}
          updatePhoneNumber={(e: ChangeEvent<HTMLInputElement>) => setPhoneNumber((e.target as HTMLInputElement).value)}
          updateCountryCode={(code: string) => setCountryCode(code || DEFAULT_COUNTRY_CODE)}
          countryCodeOptions={[
            {
              dialCode: DEFAULT_COUNTRY_CODE,
              isoCode: "NO"
            },
            ...countries.sort((a, b) => a.isoCode.localeCompare(b.isoCode))
          ].map((e) => ({
            value: e.dialCode,
            text: `${e.isoCode} ${e.dialCode}`
          }))}
          email={email}
          phoneNumber={formatPhoneNumber(countryCode, phoneNumber)}
          countryCode={countryCode}
          account={personalAccount}
          isPersonal={account?.isPersonal}
        />
      )}

      {activeStep === Steps.VerifyEmail && profile && (
        <VerifyEmail
          onClose={onClose}
          resendCode={() =>
            submitContactInformation(profile.email, profile.countryCode, cleanupPhoneNumber(profile.phoneNumber))
          }
          email={profile.email}
          onSuccess={() => {
            onSuccess();
            goToStep(Steps.AddProfileSucceeded);
          }}
        />
      )}
      {activeStep === Steps.AddProfileSucceeded && profile && account && (
        <AddProfileSucceeded
          onClose={onClose}
          email={profile.email}
          orgName={selectedAccountInfo ? selectedAccountInfo.name : ""}
          phoneNumber={formatPhoneNumber(profile.countryCode, profile.phoneNumber)}
          countryCode={profile.countryCode}
          accountName={account.name}
          isPersonal={account.isPersonal}
        />
      )}
    </Dialog>
  );
};
export default AddProfileModal;
